"use client"

import { useEffect, useState, useRef } from "react"
import { cn } from "@/lib/utils"

interface TypewriterProps {
  messages: string[]
  typeSpeed?: number
  deleteSpeed?: number
  delayBetweenMessages?: number
  cursor?: boolean
  cursorChar?: string
  className?: string
}

type Phase = "typing" | "waiting" | "deleting"

export function Typewriter({
  messages,
  typeSpeed = 40,
  deleteSpeed = 20,
  delayBetweenMessages = 7000,
  cursor = true,
  cursorChar = "|",
  className,
}: TypewriterProps) {
  const [displayText, setDisplayText] = useState("")
  const [messageIndex, setMessageIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>("typing")
  const [showCursor, setShowCursor] = useState(true)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const currentMessage = messages[messageIndex]
  const isLastMessage = messageIndex === messages.length - 1

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (phase === "typing") {
      if (displayText.length < currentMessage.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayText(currentMessage.substring(0, displayText.length + 1))
        }, typeSpeed)
      } else if (!isLastMessage) {
        // Finished typing, start waiting
        setPhase("waiting")
      }
      // If last message, just stay complete
    } else if (phase === "waiting") {
      timeoutRef.current = setTimeout(() => {
        setPhase("deleting")
      }, delayBetweenMessages)
    } else if (phase === "deleting") {
      if (displayText.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayText(displayText.substring(0, displayText.length - 1))
        }, deleteSpeed)
      } else {
        // Finished deleting, move to next message
        setMessageIndex((prev) => prev + 1)
        setPhase("typing")
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [displayText, phase, currentMessage, isLastMessage, typeSpeed, deleteSpeed, delayBetweenMessages])

  // Cursor blinking effect
  useEffect(() => {
    if (!cursor) return

    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev)
    }, 500)

    return () => clearInterval(cursorInterval)
  }, [cursor])

  return (
    <span className={cn("inline-flex", className)}>
      <span className="whitespace-pre-wrap">
        {displayText}
        {cursor && (
          <span
            className={cn(
              "ml-0.5 inline-block transition-opacity duration-100",
              showCursor ? "opacity-100" : "opacity-0"
            )}
          >
            {cursorChar}
          </span>
        )}
      </span>
    </span>
  )
}
